import { forwardRef } from 'react';

import { Alert, Spacer } from '@zillow/constellation';
import { Box, Paragraph, Text } from '@zillow/constellation-10';

import { useSelector } from '../../../selectors/useSelector';

import ListingActivationTerms from '../../ListingActivationTerms';
import SpinnerContainer from '../../SpinnerContainer';
import AlicornIframeWrapper from '../AlicornIframeWrapper';

import type { ActiveProductProps } from '../../../model/Freemium';

type Props = {
  formErrorMessage: string;
  displayedInSelectPaymentMethodModal: boolean;
};

const getFreemiumDisplayPrice = (selectedProduct: ActiveProductProps) => {
  const { duration, price } = selectedProduct;

  return (
    <>
      Your card will be charged a<strong data-testid="display-price">{` one-time fee of ${price}. `}</strong>
      By purchasing this upgrade, you will have premium benefits for up to {duration}. Deactivate your listing when you
      find a renter.
    </>
  );
};

const CreditCardCapture = forwardRef<HTMLDivElement, Props>(
  ({ formErrorMessage, displayedInSelectPaymentMethodModal }, ref) => {
    const {
      payment: {
        capture: { isFormLoaded: isIframeLoaded, zuoraUserToken, isCaptured, sessionToken, businessUnit },
      },
      properties: {
        currentProperty: {
          listingAlias,
          freemium: { isEnabled: isFreemiumEnabled, products: freemiumProducts },
          selectedProductType: freemiumSelectedProductType,
        },
      },
      app: {
        appConfig: {
          config: {
            features: { metroidIframeButtonText },
          },
        },
      },
    } = useSelector((state) => state);

    const foundIndex = freemiumProducts.findIndex(
      (product: ActiveProductProps) => product.type === freemiumSelectedProductType,
    );
    const selectedProduct = freemiumProducts[foundIndex];

    const showSpinner = !isIframeLoaded || isCaptured;

    const renderPricingContent = () => {
      if (isFreemiumEnabled) {
        return <>{getFreemiumDisplayPrice(selectedProduct)}</>;
      }
      return null;
    };

    return (
      <Box
        ref={ref}
        css={{
          display: 'flex',
          flexDirection: 'column',
          '& #z_hppm_iframe': {
            backgroundColor: 'transparent',
            width: '100%',
          },
        }}
      >
        <Box
          css={{
            maxWidth: '100%',
            margin: '0 auto',
            padding: '200',
          }}
        >
          {showSpinner && <SpinnerContainer />}
          {!showSpinner && !displayedInSelectPaymentMethodModal && (
            <Spacer marginBottom="md" data-testid="renew-info">
              <Text>{renderPricingContent()}</Text>
            </Spacer>
          )}
          {!showSpinner && displayedInSelectPaymentMethodModal && (
            <Alert
              appearance="warning"
              marginTop="xs"
              marginBottom="md"
              body="This card will become the default payment method for all future listing premium upgrades."
              data-testid="select-payment-method-alert"
            />
          )}
          {sessionToken && (
            <AlicornIframeWrapper
              sessionToken={sessionToken}
              businessUnit={businessUnit}
              listingAlias={listingAlias}
              zuoraUserToken={zuoraUserToken}
            />
          )}
          {!showSpinner && formErrorMessage && (
            <Paragraph data-testid="error-text" css={{ marginBottom: '400', color: 'text.feedback.critical.hero' }}>
              {formErrorMessage}
            </Paragraph>
          )}
          {!showSpinner && (
            <ListingActivationTerms
              actionText={`By clicking ${metroidIframeButtonText}`}
              useFreemiumValues={isFreemiumEnabled}
            />
          )}
        </Box>
      </Box>
    );
  },
);

CreditCardCapture.displayName = 'CreditCardCapture';

export default CreditCardCapture;
